import type { Locale } from "@quality-field/shared";

/**
 * Every string the interface says.
 *
 * ⚠ Keys are English identifiers because the code is English (CLAUDE.md, *Working with
 * Jérémy*). The VALUES are the product's two languages, French first because French is the
 * default — the report is read by Réginald, Philippe, Frédéric and Peter.
 *
 * ⚠ Nothing here interpolates a number into the middle of a sentence without a placeholder.
 * `{n} lignes` is one string; `"lignes"` bolted onto a number in JSX is a sentence that
 * cannot be translated, because word order is not universal.
 *
 * ⚠ Proper nouns stay put: **Quality Field**, **Quality Audits**, the Monday board's own
 * labels (`Dispatched`, `CIF - Circet`, `P1`), and people's names. A label that differs
 * from the board by one character counts zero, silently — translating one would be worse
 * than useless.
 */

export type Dict = Record<string, string>;

const fr: Dict = {
  // ── Shell ──
  "nav.main": "Principal",
  "nav.reporting": "Rapports",
  "nav.administration": "Administration",
  "nav.reports": "Rapports",
  "nav.users": "Utilisateurs",
  "nav.reportAccess": "Accès aux rapports",
  "nav.companies": "Sociétés",
  "nav.soon": "BIENTÔT",
  "nav.signOut": "Se déconnecter",
  "shell.signedInAs": "Connecté en tant que {role}",
  "role.admin": "Administrateur",
  "role.internal": "Interne Unifiber",
  "shell.language": "Langue",

  // ── Tables and charts ──
  "table.sort.hint": "Cliquer pour trier",
  "chart.filterOn": "Filtrer sur {label}",
  "chart.clickAgain": "Re-cliquer pour retirer ce filtre",

  // ── Report catalogue ──
  "catalog.welcome": "Bon retour, {name}.",
  "catalog.loading": "Chargement…",
  "catalog.refreshedDaily": "Actualisé chaque jour",
  "catalog.comingSoon": "Bientôt",
  "catalog.open": "Ouvrir",
  "catalog.empty.title": "Aucun rapport pour l'instant",
  "catalog.empty.body":
    "Votre compte est créé et vous êtes connecté. Les rapports sont en cours de construction — Quality Audits est le prochain.",

  // ── Placeholder ──
  "notBuilt.title": "{what} n'est pas encore construit",
  "notBuilt.what.report": "Ce rapport",
  "notBuilt.body":
    "L'écran est dessiné et validé — il arrive plus tard. La navigation est réelle, pour que la forme du produit soit visible dès maintenant.",
  "notBuilt.back": "Retour aux rapports",

  // ── Quality Audits ──
  "report.loading": "Chargement…",
  "report.error.title": "Le rapport n'a pas pu être chargé",
  "report.noSnapshot.title": "Aucun relevé pour l'instant",
  "report.noSnapshot.body":
    "Le tableau est lu une fois par jour à 05:00. Rien n'a encore été enregistré, il n'y a donc rien à afficher — ce n'est pas un backlog vide.",
  "report.freshness.snapshotOf": "Relevé du {date}",
  "report.freshness.counts": "{stored} lignes dans le périmètre, sur {board} éléments du tableau",
  "report.scope.showing": "Vous voyez {labels}",
  "report.scope.none": "aucun partenaire — rien n'est rattaché à votre société",

  "report.filter.partner": "Partenaire",
  "report.filter.commune": "Commune",
  "report.filter.priority": "Priorité",
  "report.filter.unifiber": "Unifiber",
  "report.filter.contractor": "Entrepreneur",
  "report.filter.typeVlsFo": "Type VLS/FO",
  "report.filter.all": "Tous",
  "report.filter.openOnly": "Ouverts uniquement",
  "report.filter.clear": "Effacer {n}",

  "report.tile.openPunches": "Punches ouvertes",
  "report.tile.openPunches.note":
    "{n} ne portent aucune priorité sur le tableau et ne comptent que dans le total.",
  "report.tile.openPunches.note.one":
    "{n} ne porte aucune priorité sur le tableau et ne compte que dans le total.",
  "report.tile.dispatched": "Dispatchées",
  "report.tile.dispatched.note": "Sur {total} punches ouvertes ; les autres portent un autre statut Unifiber, ou aucun.",
  "report.tile.done": "Faites, en attente de validation",
  "report.tile.done.note":
    "Marquées Done par l'entrepreneur, ni validées ni renvoyées : la file de validation de Réginald. Seul son Validated clôture un élément.",
  "report.tile.done.sentBack": "{n} autres, marquées Done puis renvoyées en Redo, attendent l'entrepreneur.",
  "report.tile.done.sentBack.one": "{n} autre, marquée Done puis renvoyée en Redo, attend l'entrepreneur.",
  // ── The contractors' work (feature 29): the fourth tile opens it ──
  "report.work.tile.eyebrow": "Entrepreneurs",
  "report.work.tile.title": "Statistiques et évolution",
  "report.work.tile.open": "Ouvrir les statistiques et l'évolution des entrepreneurs",
  "report.work.title": "Statistiques et évolution des entrepreneurs",
  "report.work.intro":
    "Par mois, les tickets que chaque entrepreneur a marqués Done, hors renvoyés en Redo. Un nom : son évolution s'affiche dans « Entrants et clôturés », plus bas. Une case, une barre ou « Jamais touchés » : ces tickets sont sélectionnés, et « Voir ces tickets », dans le bandeau du bas, ferme la fenêtre sur la page qui les montre, tableau et export compris. Chaque clic filtre aussi la page ; re-cliquez pour annuler.",
  "report.work.selection.label": "Sélection",
  "report.work.selection.untouched": "jamais touchés",
  "report.work.selection.status": "statut entrepreneur {status}",
  "report.work.selection.openAndClosed": "ouverts et clôturés",
  "report.work.selection.others": "+ {n} filtres de la page",
  "report.work.selection.others.one": "+ {n} filtre de la page",
  "report.work.selection.count": "{n} tickets",
  "report.work.selection.count.one": "{n} ticket",
  "report.work.selection.show": "Voir ces tickets",
  "report.work.selection.clear": "Effacer",
  "report.work.col.contractor": "Entrepreneur",
  "report.work.col.trend": "Tendance (4 sem.)",
  "report.work.col.open": "Ouverts",
  "report.work.col.untouched": "Jamais touchés",
  "report.work.col.sentBack": "Renvoyés",
  "report.work.total": "Tous",
  "report.work.trend.up": "En hausse",
  "report.work.trend.down": "En baisse",
  "report.work.trend.flat": "Stable",
  "report.work.trend.none": "Aucune activité",
  "report.work.trend.detail": "Terminés : {recent} en {recentRange}, contre {before} en {beforeRange}",
  "report.work.name.hint": "Afficher l'évolution de {partner}",
  "report.work.name.clear": "Revenir à tous les entrepreneurs",
  "report.work.cell.hint": "{partner} · terminés en {month} : {n} — cliquez pour les voir",
  "report.work.untouched.hint": "{partner} · ouverts sans aucun statut entrepreneur : {n} — cliquez pour la liste",
  "report.work.note.trend":
    "Tendance : tickets terminés sur les 4 dernières semaines complètes ({recentRange}) contre les 4 d'avant ({beforeRange}). Survolez un badge pour ses chiffres.",
  "report.work.note.columns":
    "Jamais touchés : tickets ouverts sans aucun statut entrepreneur (ni Accepted, ni Planned, ni Refused, ni Done). Renvoyés : marqués Done puis renvoyés en Redo, ils attendent l'entrepreneur.",
  "report.work.note.declared":
    "Seul ce qui est déclaré dans Monday est visible : une réparation faite sur le terrain mais jamais marquée Done compte zéro. Le tableau suit les filtres Type VLS/FO et Unifiber ; ceux qu'un clic y pose — partenaire, commune, priorité, statut entrepreneur, période, « Ouverts uniquement » — ne le réduisent pas.",
  "report.work.note.partial": "* {month} est en cours : le relevé du {date} s'arrête à la veille au soir.",

  "report.flow.title": "Entrants et clôturés",
  "report.flow.titleFor": "Entrants et clôturés — {partner}",
  "report.flow.titleAll": "Entrants et clôturés — tous les entrepreneurs",
  "report.flow.hint":
    "Cliquez une barre pour sélectionner ces tickets — « Voir ces tickets », en bas, ferme la fenêtre sur la page filtrée. Re-cliquez la barre choisie pour l'annuler.",
  "report.flow.unit.week": "Par semaine",
  "report.flow.unit.month": "Par mois",
  "report.flow.week": "S{n}",
  "report.flow.series.created": "Entrants",
  "report.flow.series.done": "Terminés par l'entrepreneur",
  "report.flow.series.validated": "Validés par Unifiber",
  "report.flow.short.created": "Entrants",
  "report.flow.short.done": "Terminés",
  "report.flow.short.validated": "Validés",
  "report.flow.note.scope":
    "Compte les tickets ouverts et clôturés : ce graphique ignore « Ouverts uniquement » et suit tous les autres filtres.",
  "report.flow.note.lastState":
    "Chaque ticket compte à la date de son statut actuel, donc les périodes passées peuvent encore bouger : un ticket renvoyé en Redo quitte « terminés ». Les validés comprennent les refus approuvés.",
  "report.flow.note.partial": "{period} est en cours : le relevé du {date} s'arrête à la veille au soir.",
  "report.flow.note.undated":
    "{n} événements n'ont pas de date (création, Done ou validation) : ils ne sont dans aucune barre.",
  "report.flow.note.undated.one":
    "{n} événement n'a pas de date (création, Done ou validation) : il n'est dans aucune barre.",
  "report.flow.chip": "{event} · {period}",
  "report.flow.chip.clear": "Retirer ce filtre",

  "report.crumb.allPartners": "Tous les partenaires",
  "report.crumb.yourRows": "Vos lignes",
  "report.rows": "{n} lignes",
  "report.rows.one": "{n} ligne",

  "report.panel.byPartner": "Par partenaire",
  "report.panel.byCommune": "Par commune",
  "report.panel.byPriority": "Par priorité",
  "report.panel.drillHint": "Cliquez une barre pour descendre. Re-cliquez pour remonter.",
  "report.panel.deepest": "Le dernier niveau — les punches elles-mêmes sont listées en dessous.",
  "report.panel.donutTitle": "Punches ouvertes par priorité",
  "report.panel.donutHint": "Cliquez une ligne pour filtrer tout l'écran.",
  "report.donut.centre": "Punches",
  "report.legend.other": "Sans priorité",
  "report.panel.byType": "Par type VLS/FO",
  "report.panel.byTypeHint": "Cliquez une barre pour filtrer tout l'écran ; re-cliquez pour annuler.",
  "report.type.multi":
    "{multi} des {n} punches portent plusieurs types et comptent dans chacune de leurs barres : les barres totalisent {sum}, pas {n}.",
  "report.type.multi.one":
    "{multi} des {n} punches porte plusieurs types et compte dans chacune de ses barres : les barres totalisent {sum}, pas {n}.",
  "report.type.ownFilter": "Ce graphique ignore son propre filtre : les autres types y restent, en retrait.",

  "report.empty.noMatch": "Aucune ligne ne correspond à ces filtres.",
  "report.empty.noAreaRows": "Ce partenaire n'a aucune ligne ouverte ici.",

  "report.table.title": "Les lignes derrière ces chiffres",
  "report.table.capped": "Les {limit} premières sur {total} — affinez les filtres pour voir le reste",
  "report.table.address": "Adresse",
  "report.table.reference": "Réf. Monday",
  "report.table.typeVlsFo": "Type VLS/FO",
  "report.table.priority": "Priorité",
  "report.table.partner": "Partenaire",
  "report.table.commune": "Commune",
  "report.table.unifiber": "Unifiber",
  "report.table.contractor": "Entrepreneur",
  "report.panel.ageTitle": "Âge des éléments",
  "report.panel.ageHint": "Depuis leur création sur le tableau — pas depuis leur dispatch.",
  "report.age.unknown": "{n} lignes sans date de création : elles ne sont dans aucune tranche.",
  "report.panel.overdueTitle": "Dépassement des délais",
  "report.panel.overdueHint": "Compté depuis la date de dispatch, pas depuis la création.",
  "report.overdue.late": "en retard",
  "report.overdue.onTime": "dans les temps",
  "report.overdue.na": "sans délai",
  "report.overdue.worst": "Le pire dépassement est de {worst} jours ; en moyenne {average} jours au-delà du délai.",
  "report.overdue.deadlines": "Délais : P1 7 jours, P2 15 jours, P3 30 jours. « Sans délai » = les punches sans priorité et celles sans date de dispatch — leur délai est inconnu, pas respecté.",
  "report.export.button": "Exporter",
  "report.export.hint": "Télécharge un CSV de la vue affichée, prêt pour Excel",
  "report.export.locality": "Localité",
  "report.export.pop": "POP",
  "report.export.created": "Créée le",
  "report.export.doneOn": "Terminée le",
  "report.export.validatedOn": "Validée le",
  "report.export.doneTyped": "Date Done saisie",
  "report.export.validatedTyped": "Date Validated saisie",
  "report.export.open": "Ouvert",
  "report.export.yes": "oui",
  "report.export.no": "non",
  "report.table.age": "Âge",  "report.table.overdue": "Retard",
  "report.days": "{n} j",
  "report.table.dispatched": "Dispatchée le",

  "report.noPartner.note":
    "{n} éléments ouverts ne sont dispatchés à aucun partenaire : ils comptent dans ces totaux, mais dans les chiffres d'aucun partenaire.",
  "report.noPartner.note.one":
    "{n} élément ouvert n'est dispatché à aucun partenaire : il compte dans ces totaux, mais dans les chiffres d'aucun partenaire.",

  // Group headings for rows whose column is empty. Not board labels — our own wording.
  "group.noPartner": "Sans partenaire",
  "group.noCommune": "Sans commune",
  "group.noPriority": "Sans priorité",
  "group.noType": "Sans type",
  "group.noStatus": "Sans statut",
  "group.noAnswer": "Sans réponse",
  "group.notSet": "Non renseigné",
};

const en: Dict = {
  "nav.main": "Main",
  "nav.reporting": "Reporting",
  "nav.administration": "Administration",
  "nav.reports": "Reports",
  "nav.users": "Users",
  "nav.reportAccess": "Report Access",
  "nav.companies": "Companies",
  "nav.soon": "SOON",
  "nav.signOut": "Sign out",
  "shell.signedInAs": "Signed in as {role}",
  "role.admin": "Administrator",
  "role.internal": "Unifiber Internal",
  "shell.language": "Language",

  "table.sort.hint": "Click to sort",
  "chart.filterOn": "Filter on {label}",
  "chart.clickAgain": "Click again to clear this filter",

  "catalog.welcome": "Welcome back, {name}.",
  "catalog.loading": "Loading…",
  "catalog.refreshedDaily": "Refreshed daily",
  "catalog.comingSoon": "Coming soon",
  "catalog.open": "Open",
  "catalog.empty.title": "No reports yet",
  "catalog.empty.body":
    "Your account is set up and signed in. The reports are still being built — Quality Audits is next.",

  "notBuilt.title": "{what} is not built yet",
  "notBuilt.what.report": "This report",
  "notBuilt.body":
    "The screen is designed and validated — it arrives later. The navigation is real so the shape of the product is reviewable now.",
  "notBuilt.back": "Back to reports",

  "report.loading": "Loading…",
  "report.error.title": "The report could not be loaded",
  "report.noSnapshot.title": "No snapshot yet",
  "report.noSnapshot.body":
    "The board is read once a day at 05:00. Nothing has been recorded yet, so there is nothing to report — this is not an empty backlog.",
  "report.freshness.snapshotOf": "Snapshot of {date}",
  "report.freshness.counts": "{stored} rows in scope, from {board} items on the board",
  "report.scope.showing": "Showing {labels}",
  "report.scope.none": "no partner — nothing is mapped to your company",

  "report.filter.partner": "Partner",
  "report.filter.commune": "Commune",
  "report.filter.priority": "Priority",
  "report.filter.unifiber": "Unifiber",
  "report.filter.contractor": "Contractor",
  "report.filter.typeVlsFo": "Type VLS/FO",
  "report.filter.all": "All",
  "report.filter.openOnly": "Open only",
  "report.filter.clear": "Clear {n}",

  "report.tile.openPunches": "Open punches",
  "report.tile.openPunches.note": "{n} carry no priority on the board and are counted in the total only.",
  "report.tile.openPunches.note.one": "{n} carries no priority on the board and is counted in the total only.",
  "report.tile.dispatched": "Dispatched",
  "report.tile.dispatched.note": "Of {total} open punches; the others carry another Unifiber status, or none yet.",
  "report.tile.done": "Done, awaiting validation",
  "report.tile.done.note":
    "Marked Done by the contractor, neither validated nor sent back: Réginald's validation queue. Only his Validated closes an item.",
  "report.tile.done.sentBack": "{n} more, marked Done then sent back with Redo, await the contractor.",
  "report.tile.done.sentBack.one": "{n} more, marked Done then sent back with Redo, awaits the contractor.",
  "report.work.tile.eyebrow": "Contractors",
  "report.work.tile.title": "Statistics and trends",
  "report.work.tile.open": "Open the contractors' statistics and trends",
  "report.work.title": "Contractors: statistics and trends",
  "report.work.intro":
    "By month, the tickets each contractor marked Done, leaving out those sent back with Redo. A name: their evolution shows in \"In and out\" below. A cell, a bar or \"Never touched\": those tickets are selected, and \"See these tickets\", in the banner at the bottom, closes the dialog on the page showing them, table and export included. Every click also filters the page; click again to clear.",
  "report.work.selection.label": "Selection",
  "report.work.selection.untouched": "never touched",
  "report.work.selection.status": "contractor status {status}",
  "report.work.selection.openAndClosed": "open and closed",
  "report.work.selection.others": "+ {n} page filters",
  "report.work.selection.others.one": "+ {n} page filter",
  "report.work.selection.count": "{n} tickets",
  "report.work.selection.count.one": "{n} ticket",
  "report.work.selection.show": "See these tickets",
  "report.work.selection.clear": "Clear",
  "report.work.col.contractor": "Contractor",
  "report.work.col.trend": "Trend (4 wk)",
  "report.work.col.open": "Open",
  "report.work.col.untouched": "Never touched",
  "report.work.col.sentBack": "Sent back",
  "report.work.total": "All",
  "report.work.trend.up": "Rising",
  "report.work.trend.down": "Falling",
  "report.work.trend.flat": "Steady",
  "report.work.trend.none": "No activity",
  "report.work.trend.detail": "Done: {recent} in {recentRange}, against {before} in {beforeRange}",
  "report.work.name.hint": "Show {partner}'s evolution",
  "report.work.name.clear": "Back to all contractors",
  "report.work.cell.hint": "{partner} · done in {month}: {n} — click to see them",
  "report.work.untouched.hint": "{partner} · open with no contractor status: {n} — click for the list",
  "report.work.note.trend":
    "Trend: tickets done in the last 4 complete weeks ({recentRange}) against the 4 before ({beforeRange}). Hover a badge for its figures.",
  "report.work.note.columns":
    "Never touched: open tickets with no contractor status at all (no Accepted, Planned, Refused or Done). Sent back: marked Done then sent back with Redo; they wait for the contractor.",
  "report.work.note.declared":
    "Only what is declared in Monday is visible: a fix made on site but never marked Done counts zero. The table follows the Type VLS/FO and Unifiber filters; those its clicks set — partner, commune, priority, contractor status, period, \"Open only\" — do not narrow it.",
  "report.work.note.partial": "* {month} is in progress: the snapshot of {date} stops at the evening before.",

  "report.flow.title": "In and out",
  "report.flow.titleFor": "In and out — {partner}",
  "report.flow.titleAll": "In and out — all contractors",
  "report.flow.hint":
    "Click a bar to select those tickets — \"See these tickets\", at the bottom, closes the dialog on the filtered page. Click the chosen bar again to clear it.",
  "report.flow.unit.week": "By week",
  "report.flow.unit.month": "By month",
  "report.flow.week": "W{n}",
  "report.flow.series.created": "Incoming",
  "report.flow.series.done": "Done by the contractor",
  "report.flow.series.validated": "Validated by Unifiber",
  "report.flow.short.created": "Incoming",
  "report.flow.short.done": "Done",
  "report.flow.short.validated": "Validated",
  "report.flow.note.scope":
    "Counts open and closed tickets: this chart ignores \"Open only\" and follows every other filter.",
  "report.flow.note.lastState":
    "Each ticket counts on the date of its current status, so past periods can still move: a ticket sent back with Redo leaves \"done\". Validated includes approved refusals.",
  "report.flow.note.partial": "{period} is in progress: the snapshot of {date} stops at the evening before.",
  "report.flow.note.undated": "{n} events have no date (creation, Done or validation): they are in no bar.",
  "report.flow.note.undated.one": "{n} event has no date (creation, Done or validation): it is in no bar.",
  "report.flow.chip": "{event} · {period}",
  "report.flow.chip.clear": "Remove this filter",

  "report.crumb.allPartners": "All partners",
  "report.crumb.yourRows": "Your rows",
  "report.rows": "{n} rows",
  "report.rows.one": "{n} row",

  "report.panel.byPartner": "By partner",
  "report.panel.byCommune": "By commune",
  "report.panel.byPriority": "By priority",
  "report.panel.drillHint": "Click a bar to drill in. Click it again to come back up.",
  "report.panel.deepest": "The deepest level — the punches themselves are listed below.",
  "report.panel.donutTitle": "Open punches by priority",
  "report.panel.donutHint": "Click a row to filter the whole screen.",
  "report.donut.centre": "Punches",
  "report.legend.other": "No priority",
  "report.panel.byType": "By Type VLS/FO",
  "report.panel.byTypeHint": "Click a bar to filter the whole screen; click it again to clear.",
  "report.type.multi":
    "{multi} of the {n} punches carry several types and count in each of their bars: the bars add up to {sum}, not {n}.",
  "report.type.multi.one":
    "{multi} of the {n} punches carries several types and counts in each of its bars: the bars add up to {sum}, not {n}.",
  "report.type.ownFilter": "This chart ignores its own filter: the other types stay on it, dimmed.",

  "report.empty.noMatch": "No rows match these filters.",
  "report.empty.noAreaRows": "This partner has no open rows here.",

  "report.table.title": "The rows behind these figures",
  "report.table.capped": "Showing the first {limit} of {total} — narrow the filters to see the rest",
  "report.table.address": "Address",
  "report.table.reference": "Monday ref.",
  "report.table.typeVlsFo": "Type VLS/FO",
  "report.table.priority": "Priority",
  "report.table.partner": "Partner",
  "report.table.commune": "Commune",
  "report.table.unifiber": "Unifiber",
  "report.table.contractor": "Contractor",
  "report.panel.ageTitle": "Age of items",
  "report.panel.ageHint": "Since they were created on the board — not since they were dispatched.",
  "report.age.unknown": "{n} rows have no creation date: they are in no bucket.",
  "report.panel.overdueTitle": "Deadline breaches",
  "report.panel.overdueHint": "Counted from the dispatch date, not from creation.",
  "report.overdue.late": "late",
  "report.overdue.onTime": "on time",
  "report.overdue.na": "no deadline",
  "report.overdue.worst": "The worst breach is {worst} days; on average {average} days past the deadline.",
  "report.overdue.deadlines": "Deadlines: P1 7 days, P2 15 days, P3 30 days. \"No deadline\" = the punches with no priority and those with no dispatch date — their deadline is unknown, not met.",
  "report.export.button": "Export",
  "report.export.hint": "Downloads a CSV of what is on screen, ready for Excel",
  "report.export.locality": "Locality",
  "report.export.pop": "POP",
  "report.export.created": "Created",
  "report.export.doneOn": "Done on",
  "report.export.validatedOn": "Validated on",
  "report.export.doneTyped": "Done date (typed)",
  "report.export.validatedTyped": "Validated date (typed)",
  "report.export.open": "Open",
  "report.export.yes": "yes",
  "report.export.no": "no",
  "report.table.age": "Age",  "report.table.overdue": "Overdue",
  "report.days": "{n} d",
  "report.table.dispatched": "Dispatched",

  "report.noPartner.note":
    "{n} open items are dispatched to no partner, so they appear in these totals but in no partner's own figures.",
  "report.noPartner.note.one":
    "{n} open item is dispatched to no partner, so it appears in these totals but in no partner's own figures.",

  "group.noPartner": "No partner",
  "group.noCommune": "No commune",
  "group.noPriority": "No priority",
  "group.noType": "No type",
  "group.noStatus": "No status",
  "group.noAnswer": "No answer",
  "group.notSet": "Not set",
};

export const CATALOG: Record<Locale, Dict> = { fr, en };
